import { Button, Icon, Row } from "@/ui";
import React, { useState } from 'react';
import { authService } from '../services';
import { toast } from 'sonner';
import { logger } from '@/shared/lib/logger/logger';
import { Loader2 } from 'lucide-react';
import type { SocialProvider } from '../types';

interface SocialLoginButtonProps {
  provider: SocialProvider;
}

/**
 * Get user-friendly error message for auth errors
 */
function getAuthErrorMessage(error: Error, provider: string): string {
  const message = error.message.toLowerCase();

  // Firebase not configured - show helpful message
  if (message.includes('firebase is not configured') || message.includes('missing firebase')) {
    return 'Authentication is not available at the moment. Please try again later or use email sign-in.';
  }

  // Popup blocked
  if (message.includes('popup') && message.includes('blocked')) {
    return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
  }

  // Network error
  if (message.includes('network') || message.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Account exists with different credential
  if (message.includes('account-exists-with-different-credential')) {
    return 'An account already exists with this email using a different sign-in method.';
  }

  // Generic auth error
  return `Could not sign in with ${provider}. Please try again.`;
}

export const SocialLoginButton: React.FC<SocialLoginButtonProps> = ({ provider }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const { error } = await authService.signInWithProvider({
        provider,
        redirectTo: `${window.location.origin}/auth/callback`
      });

      if (error) {
        // If popup was closed by user, don't show error
        if (error instanceof Error && error.message.includes('popup-closed')) {
          setIsLoading(false);
          return;
        }
        throw error;
      }

      // For popup flow (Firebase Auth), user stays on same page
      // Auth state change will be handled by onAuthStateChange listener
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      // Show user-friendly error toast
      const errorMessage = error instanceof Error
        ? getAuthErrorMessage(error, provider)
        : `Could not sign in with ${provider}. Please try again.`;

      toast.error('Sign in failed', {
        description: errorMessage,
      });

      logger.error(`[SocialLoginButton] Failed to sign in with ${provider}`, error instanceof Error ? error : new Error(String(error)), {
        component: 'SocialLoginButton',
        action: 'social_signin',
        provider,
      });
    }
  };

  const getProviderConfig = (): { icon: React.ReactNode; label: string; className: string } => {
    switch (provider) {
      case 'google':
        return {
          icon: (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          ),
          label: 'Continue with Google',
          className: 'hover:bg-gray-50 :bg-gray-800'
        };
      case 'github':
        return {
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd" />
            </svg>
          ),
          label: 'Continue with GitHub',
          className: 'hover:bg-gray-50 :bg-gray-800'
        };
    }
  };

  const config = getProviderConfig();

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => void handleLogin()}
      disabled={isLoading}
      fullWidth
      size="lg"
      className="relative"
    >
      {isLoading ? (
        <Row align="center" gap={2} justify="center">
          <Icon icon={Loader2} size="md" animate="spin" />
          <span>Connecting...</span>
        </Row>
      ) : (
        <Row align="center" gap={2} justify="center" style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '1rem' }}>{config.icon}</span>
          <span style={{ fontWeight: 500 }}>{config.label}</span>
        </Row>
      )}
    </Button>
  );
};
